import { useStore } from 'app/context';
import { UserModel } from 'app/models/core/user.model';
import { isRelevantUser } from './UsageAnmeldequoteShared';
import { UsageCompareRows, UsageSlide, UsageSource } from './UsageReportShared';
import { formatNum } from 'app/utils';
import { observer } from 'mobx-react';
import moment from 'moment';
import { useEffect, useState } from 'react';

interface QuoteRow {
	label: string;
	rank: number;
	activeCount: number;
	loginCount: number;
	prevLoginCount: number;
}

const quote = (logins: number, active: number): number => {
	return active > 0 ? Math.round((logins / active) * 100) : 0;
};

const groupSortOrder = (group: string): number => {
	switch (group) {
		case 'vbl':
			return 0;
		case 'spezi':
			return 1;
		case 'multi':
			return 2;
		default:
			return 3;
	}
};

const buildRows = (users: UserModel[], currLoginIds: number[], prevLoginIds: number[], keyOf: (u: UserModel) => { label: string; rank: number } | undefined): QuoteRow[] => {
	const currSet = new Set(currLoginIds);
	const prevSet = new Set(prevLoginIds);

	const byKey = new Map<string, QuoteRow>();
	users.forEach((u) => {
		if (!isRelevantUser(u)) {
			return;
		}
		const key = keyOf(u);
		if (!key) {
			return;
		}
		let row = byKey.get(key.label);
		if (!row) {
			row = { label: key.label, rank: key.rank, activeCount: 0, loginCount: 0, prevLoginCount: 0 };
			byKey.set(key.label, row);
		}
		row.activeCount++;
		if (currSet.has(u.bnrId)) {
			row.loginCount++;
		}
		if (prevSet.has(u.bnrId)) {
			row.prevLoginCount++;
		}
	});

	return Array.from(byKey.values()).sort((a, b) => a.rank - b.rank || a.label.localeCompare(b.label));
};

const QuoteSlide = (props: { title: string; rows: QuoteRow[]; currYear: number; prevYear: number }) => {
	const { rows, currYear, prevYear } = props;

	const totalActive = rows.reduce((sum, r) => sum + r.activeCount, 0);
	const totalLogins = rows.reduce((sum, r) => sum + r.loginCount, 0);
	const totalPrevLogins = rows.reduce((sum, r) => sum + r.prevLoginCount, 0);

	// Ranking nach aktueller Quote — liest sich wie ein Bericht, statt der Gruppen-Sortierung
	const compareRows = rows
		.map((r) => {
			const curr = quote(r.loginCount, r.activeCount);
			const prev = quote(r.prevLoginCount, r.activeCount);
			const d = curr - prev;
			return {
				label: r.label,
				curr,
				prev,
				delta: { text: `${d > 0 ? '+' : ''}${d}`, dir: d > 0 ? ('up' as const) : d < 0 ? ('down' as const) : ('flat' as const) },
			};
		})
		.sort((a, b) => b.curr - a.curr || a.label.localeCompare(b.label));

	// Ø ABV als Linie: Mittelwert der beiden Jahresquoten
	const abvAvg = Math.round((quote(totalLogins, totalActive) + quote(totalPrevLogins, totalActive)) / 2);

	return (
		<UsageSlide title={props.title} source={UsageSource.dvs}>
			<UsageCompareRows rows={compareRows} currLabel={`${currYear}`} prevLabel={`${prevYear}`} showTicks={true} avg={{ value: abvAvg, label: `Ø ABV ${abvAvg}%` }} />
		</UsageSlide>
	);
};

// Folien "Anmeldequote nach Funktionsgruppe" und "Anmeldequote nach VD".
// Quote = Anteil der aktiven Benutzer mit mindestens einer Anmeldung seit dem 1.1.,
// Vorjahresquote über den gleichen Zeitraum (1.1. bis heute), damit YTD vergleichbar ist.
export const UsageAnmeldequoteCharts = observer(() => {
	const { adminLogStore, adminUserStore } = useStore();
	const [steartRows, setSteartRows] = useState<QuoteRow[]>([]);
	const [vdRows, setVdRows] = useState<QuoteRow[]>([]);
	const [loading, setLoading] = useState(true);

	const currYear = moment().year();
	const prevYear = currYear - 1;

	useEffect(() => {
		const load = async () => {
			setLoading(true);
			const [users, currLoginIds, prevLoginIds] = await Promise.all([
				adminUserStore.findAllActiveUsers(),
				adminLogStore.loadLoginBnrIds(`${currYear}-01-01`, moment().add(1, 'day').format('YYYY-MM-DD')),
				adminLogStore.loadLoginBnrIds(`${prevYear}-01-01`, moment().subtract(1, 'year').add(1, 'day').format('YYYY-MM-DD')),
			]);
			setSteartRows(
				buildRows(users, currLoginIds, prevLoginIds, (u) => {
					// Multi-Stearts (LDP, EDP, EDT) sind interne Rollen und für die Quote nicht relevant
					if (u.steart.group === 'multi') {
						return undefined;
					}
					return {
						label: u.steart.name,
						rank: groupSortOrder(u.steart.group) * 1000 + u.steart.sortOrder,
					};
				}),
			);
			setVdRows(buildRows(users, currLoginIds, prevLoginIds, (u) => (u.firstVd ? { label: u.firstVd, rank: 0 } : undefined)));
			setLoading(false);
		};
		load();
	}, [adminLogStore, adminUserStore, currYear, prevYear]);

	if (loading) {
		return <div>Wird geladen...</div>;
	}

	return (
		<>
			<QuoteSlide title="Anmeldequote nach Funktionsgruppe" rows={steartRows} currYear={currYear} prevYear={prevYear} />
			<QuoteSlide title="Anmeldequote nach VD" rows={vdRows} currYear={currYear} prevYear={prevYear} />
		</>
	);
});
